import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from "@react-google-maps/api";
import {
  MAP_LIBRARIES,
  START_FLAG,
  END_FLAG,
  buildRoute,
  isTower,
  pointPosition,
  MAP_FALLBACK_CENTER,
  RouteLines,
  RouteFlags,
  RouteLegend,
} from "components/RoutePlot";

const mapContainerStyle = { height: "500px", width: "100%" };

const rotationCache = {};
const getRotatedIcon = (imageUrl, angle) => {
  if (!imageUrl) return null;
  const roundedAngle = Math.round(angle / 5) * 5;
  const cacheKey = `${imageUrl}_${roundedAngle}`;

  if (rotationCache[cacheKey]) return Promise.resolve(rotationCache[cacheKey]);

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const img = new Image();
  
  if (!imageUrl.startsWith('data:')) {
      img.crossOrigin = "Anonymous";
  }

  // Start the Promise BEFORE setting the src
  return new Promise((resolve) => {
    img.onload = () => {
      // 1. Calculate the diagonal length of the image using Pythagorean theorem.
      // This guarantees the image will never clip, even at a 45-degree angle!
      const maxDim = Math.ceil(Math.sqrt(img.width * img.width + img.height * img.height));

      // 2. Make the canvas a perfect square
      canvas.width = maxDim;
      canvas.height = maxDim;
      
      // 3. Move the pivot point to the exact center of the new square
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((roundedAngle * Math.PI) / 180);
      
      // 4. Draw the image perfectly centered inside the square
      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      
      const rotatedDataUrl = canvas.toDataURL("image/png");
      rotationCache[cacheKey] = rotatedDataUrl;
      resolve(rotatedDataUrl);
    };
    
    img.onerror = () => {
      resolve(imageUrl); 
    };

    img.src = imageUrl;
  });
};


const RouteMapView = ({
  mapInfo,
  isPlaying,
  speed,
  currentIndex,
  setCurrentIndex,
  overlayImg,
}) => {
  const [carPosition, setCarPosition] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [rotatedCarImg, setRotatedCarImg] = useState(overlayImg);
  const [safeBase64Img, setSafeBase64Img] = useState(null);
  
  const mapRef = useRef(null);
  const indexRef = useRef(currentIndex); 
  const animationRef = useRef(null);
  const delayRef = useRef(null);
  const stepRef = useRef(0);
  const isPlayingRef = useRef(isPlaying);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    indexRef.current = currentIndex;
  }, [currentIndex]);

  useEffect(() => {
    if (!overlayImg) return;

    // THE FIX: Append a timestamp to the URL. 
    // Chrome will think this is a brand new file and will bypass its poisoned disk cache,
    // forcing S3 to serve the fresh image with the correct CORS headers you already configured.
    const cacheBustedUrl = overlayImg.includes('?') 
      ? `${overlayImg}&t=${Date.now()}` 
      : `${overlayImg}?t=${Date.now()}`;

    // Fetch the cache-busted URL with explicit CORS mode
    fetch(cacheBustedUrl, { mode: 'cors' })
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");
        return res.blob();
      })
      .then((blob) => {
        const reader = new FileReader();
        reader.onloadend = () => {
           setSafeBase64Img(reader.result); 
           setRotatedCarImg(reader.result); 
        };
        reader.readAsDataURL(blob);
      })
      .catch((err) => {
        console.warn("Failed to fetch image with CORS. Check S3 policy.", err);
        setSafeBase64Img(overlayImg); 
        setRotatedCarImg(overlayImg);
      });
  }, [overlayImg]);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: MAP_LIBRARIES,
    version: "3.64",
  });

  const routeModel = useMemo(
    () => buildRoute(mapInfo),
    [mapInfo]
  );

  // --- FIX 1: The Memory Lock for Map Center ---
  // This prevents React from forcing the camera back to the start 60 times a second
  const initialCenter = useMemo(() => {
    for (const point of mapInfo) {
      const position = pointPosition(point);
      if (position) return position;
    }
    return MAP_FALLBACK_CENTER;
  }, [mapInfo]);

  const poleIcon = useMemo(() => {
    if (!isLoaded || !window.google) return null;
    const size = { scaledSize: new window.google.maps.Size(28, 36), anchor: new window.google.maps.Point(8, 34) };
    return { start: { url: START_FLAG, ...size }, end: { url: END_FLAG, ...size } };
  }, [isLoaded]);


  // Calculate Rotation only on index change
  useEffect(() => {
    // Wait until we have the safeBase64Img before trying to rotate
    if (!mapInfo[indexRef.current] || !mapInfo[indexRef.current + 1] || !window.google || !safeBase64Img) return;
    
    const start = pointPosition(mapInfo[indexRef.current]);
    const end = pointPosition(mapInfo[indexRef.current + 1]);
    if (!start || !end) return;

    const heading = window.google.maps.geometry.spherical.computeHeading(
        new window.google.maps.LatLng(start.lat, start.lng),
        new window.google.maps.LatLng(end.lat, end.lng)
    );

    // Use the Base64 image here!
    getRotatedIcon(safeBase64Img, heading).then((newImg) => setRotatedCarImg(newImg));
  }, [currentIndex, mapInfo, safeBase64Img]); // Add safeBase64Img to dependencies

  const animateCar = useCallback(() => {
    if (!isPlayingRef.current || indexRef.current >= mapInfo.length - 1) return;

    const start = pointPosition(mapInfo[indexRef.current]);
    const end = pointPosition(mapInfo[indexRef.current + 1]);

    if (!start || !end) {
        indexRef.current += 1;
        setCurrentIndex(indexRef.current);
        return;
    }

    const animationSteps = 30; 
    stepRef.current += 1;
    const progress = stepRef.current / animationSteps;

    const interpolated = window.google.maps.geometry.spherical.interpolate(
      new window.google.maps.LatLng(start.lat, start.lng),
      new window.google.maps.LatLng(end.lat, end.lng),
      progress
    );

    setCarPosition({ lat: interpolated.lat(), lng: interpolated.lng() });

    if (progress >= 1) {
      stepRef.current = 0; 
      indexRef.current += 1; 
      setCurrentIndex(indexRef.current); 
      
      // --- FIX 2: The Smart Camera ---
      // Only pan the camera if the car is about to drive off the visible map bounds!
      if (mapRef.current) {
        const nextPoint = new window.google.maps.LatLng(end.lat, end.lng);
        const currentBounds = mapRef.current.getBounds();
        
        if (currentBounds && !currentBounds.contains(nextPoint)) {
            mapRef.current.panTo(nextPoint);
        }
      }

      delayRef.current = setTimeout(() => {
        if (isPlayingRef.current) {
            animationRef.current = requestAnimationFrame(animateCar);
        }
      }, speed / 10); 
    } else {
      animationRef.current = requestAnimationFrame(animateCar);
    }
  }, [mapInfo, speed, setCurrentIndex]);

  useEffect(() => {
    if (isPlaying && isLoaded) {
      animationRef.current = requestAnimationFrame(animateCar);
    }
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (delayRef.current) clearTimeout(delayRef.current);
    };
  }, [isPlaying, animateCar, isLoaded]);

  if (!isLoaded) return <div>Loading map...</div>;

  const ends = routeModel.segments.length ? routeModel.segments : null;
  const startPoint = ends ? ends[0].path[0] : null;
  const endSegment = ends ? ends[ends.length - 1] : null;
  const endPoint = endSegment ? endSegment.path[endSegment.path.length - 1] : null;
  return (
    <div className="relative">
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={initialCenter}
      zoom={16}
      onLoad={(map) => { mapRef.current = map; }} 
    >
      <RouteLines segments={routeModel.segments} />
      <RouteFlags flags={routeModel.flags} onSelect={setSelectedMarker} />

      {startPoint && (
        <Marker position={startPoint} zIndex={4} title="Start" icon={poleIcon?.start} />
      )}
      {endPoint && (
        <Marker position={endPoint} zIndex={4} title="End" icon={poleIcon?.end} />
      )}
      {/* The Moving Vehicle */}
      {(carPosition || startPoint) && (
        <Marker
          position={carPosition || startPoint}
          icon={{ 
            url: rotatedCarImg, 
            // FIX: Make the Google Maps container a perfect square too!
            scaledSize: new window.google.maps.Size(60, 60),
            // FIX: Anchor it exactly in the middle of the new 60x60 square
            anchor: new window.google.maps.Point(30, 30) 
          }}
        />
      )}  

      {selectedMarker && (
        <InfoWindow
          position={selectedMarker.position}
          onCloseClick={() => setSelectedMarker(null)}
        >
          <div>
            <b>{selectedMarker.kind ? `${selectedMarker.kind.code} ${selectedMarker.kind.label}` : (selectedMarker.batteryLow ? "Battery low" : "Point")}</b><br />
            <b>Time:</b> {selectedMarker.point.timestamp}<br />
            <b>Speed:</b> {selectedMarker.point.speed} km/h<br />
            <b>Position:</b> {isTower(selectedMarker.point) ? "TOWER" : "GPS"}<br />
            {selectedMarker.level != null && <><b>Battery:</b> {selectedMarker.level}%</>}
            {selectedMarker.kind && selectedMarker.batteryLow && <><br /><b>Battery low</b></>}
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
    <RouteLegend />
    </div>
  );
};

export default RouteMapView;